import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabaseWithAuth } from '@/utils/supabaseWrapper';
import { useAuth } from './useAuth';
import { useCampaignsWithPolling } from './useCampaignsWithPolling';
import { useCampaignsQuery } from './queries/useCampaignsQuery';
import { useNotificationsQuery } from './queries/useNotificationsQuery';
import { CampaignStatus } from '@/types/states';
import { CacheService } from '@/services';
import { logger } from '@/utils/productionLogger';
import { isFeatureEnabled } from '@/config/features';

interface DashboardCampaign {
  id: string;
  title: string;
  description: string;
  candidate_name: string;
  candidate_email: string;
  target_position: string;
  status: CampaignStatus;
  is_draft?: boolean | null;
  organization_id: string;
  created_at: string;
  updated_at: string;
  evaluation_criteria: any[];
  last_activity: string | null;
  unique_link: string | null;
  ai_extracted_data: any;
  referents_count: number;
  completed_referents: number;
  referents_requested_count?: number;
  refused_referents?: number;
}

interface ActivityNotification {
  id: string;
  type: string;
  message: string;
  is_read: boolean;
  created_at: string | null;
  campaign_id: string | null;
  referent_id: string | null;
}

interface DashboardStats {
  credits_remaining: number;
  refused_campaigns: number;
  pending_candidate_campaigns: number;
  pending_referents_campaigns: number;
  completed_campaigns: number;
}

export const useDashboardWithPolling = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  // Vérifier si on utilise le nouveau système
  const useNewSystem = isFeatureEnabled('REACT_QUERY_REFETCH', user?.id) || 
                        isFeatureEnabled('REALTIME_ENABLED', user?.id);

  // Nouveau système (React Query + Realtime)
  const newCampaigns = useCampaignsQuery({ includeCompleted: true });
  const newNotifications = useNotificationsQuery();

  // Ancien système (Polling)
  const { 
    campaigns: oldCampaigns, 
    loading: oldCampaignsLoading, 
    refresh: oldRefreshCampaigns,
    isConnected: oldIsConnected
  } = useCampaignsWithPolling({ 
    enabled: !useNewSystem && !!user?.id, // Désactiver si nouveau système actif
    includeCompleted: true,
    pollingInterval: 5000
  });

  // Choisir le système selon feature flag
  const rawCampaigns = useNewSystem ? (newCampaigns.data || []) : oldCampaigns;
  
  // ✅ FIX FLASH "Aucune campagne" : utiliser isPending + isFetching pour loading
  // isPending = query pas encore démarrée OU en cours sans cache
  // isFetching = requête en cours (même avec cache)
  // Résultat : skeleton affiché dès le premier tick, pas de flash empty state
  const campaignsLoading = useNewSystem 
    ? (newCampaigns.isPending || newCampaigns.isFetching)
    : oldCampaignsLoading;
  
  // ✅ Exposer isFetched pour permettre au composant de gater l'empty state
  const campaignsFetched = useNewSystem ? newCampaigns.isFetched : !oldCampaignsLoading;
  
  const refreshCampaigns = useNewSystem ? newCampaigns.refetch : oldRefreshCampaigns;
  const isConnected = useNewSystem ? true : oldIsConnected;

  // ✅ Créer une clé stable basée sur les IDs et statuts des campagnes
  const campaignsKey = useMemo(() => {
    return rawCampaigns
      .map(c => `${c.id}-${c.status}-${c.updated_at}`)
      .join('|');
  }, [rawCampaigns]);

  // Transformer les campagnes au format attendu par le dashboard
  const campaigns: DashboardCampaign[] = rawCampaigns.map(campaign => {
    const referents = campaign.referents || [];
    const referentsCount = referents.length;
    const completedReferents = referents.filter(r => r.status === 'Complété').length;
    const refusedReferents = referents.filter(r => r.refused_at !== null).length;

    return {
      id: campaign.id,
      title: campaign.candidate_name || '',
      description: campaign.target_position || '',
      candidate_name: campaign.candidate_name,
      candidate_email: campaign.candidate_email || '',
      target_position: campaign.target_position,
      status: campaign.status as CampaignStatus,
      is_draft: campaign.is_draft,
      organization_id: '',
      created_at: campaign.created_at || new Date().toISOString(),
      updated_at: campaign.updated_at || new Date().toISOString(),
      evaluation_criteria: [],
      last_activity: campaign.updated_at || new Date().toISOString(),
      unique_link: null,
      ai_extracted_data: campaign.ai_extracted_data,
      referents_count: referentsCount,
      completed_referents: completedReferents,
      referents_requested_count: campaign.referents_requested_count || 3,
      refused_referents: refusedReferents,
    };
  });

  // Calculer les stats à partir des campagnes
  useEffect(() => {
    if (!user?.id) return;
    if (!rawCampaigns || rawCampaigns.length === 0) return;

    const calculateStats = async () => {
      try {
        // Calculer les stats de campagnes
        const refusedCampaigns = rawCampaigns.filter(c => c.status === CampaignStatus.REFUSE).length;
        const pendingCandidateCampaigns = rawCampaigns.filter(c => c.status === CampaignStatus.EN_ATTENTE_CANDIDAT).length;
        const pendingReferentsCampaigns = rawCampaigns.filter(c => c.status === CampaignStatus.EN_ATTENTE_REFERENTS).length;
        const completedCampaigns = rawCampaigns.filter(c => c.status === CampaignStatus.TERMINE).length;

        // Récupérer les crédits (avec cache)
        const creditsCacheKey = CacheService.generateKey('user_credits', user.id);
        let creditsRemaining = CacheService.get<number>(creditsCacheKey);

        if (creditsRemaining === null) {
          const { data: credits } = await supabaseWithAuth.executeWithRetry(async () => {
            return supabaseWithAuth
              .from('recruiter_credits' as any)
              .select('credits_remaining')
              .eq('user_id', user.id)
              .single();
          }, 'dashboard.credits');

          creditsRemaining = (credits as any)?.credits_remaining || 0;
          CacheService.set(creditsCacheKey, creditsRemaining, 2 * 60 * 1000); // Cache 2 minutes
        }

        setStats({
          credits_remaining: creditsRemaining || 0,
          refused_campaigns: refusedCampaigns,
          pending_candidate_campaigns: pendingCandidateCampaigns,
          pending_referents_campaigns: pendingReferentsCampaigns,
          completed_campaigns: completedCampaigns
        });

      } catch (_error) {
        logger.error('Erreur calcul stats dashboard', _error as Error);
      }
    };

    calculateStats();
  }, [user?.id, campaignsKey]); // ✅ Dépendre de la clé stable au lieu du tableau complet

  // Notifications : utiliser le nouveau système ou l'ancien
  const [oldNotifications, setOldNotifications] = useState<ActivityNotification[]>([]);
  const [oldNotificationsLoading, setOldNotificationsLoading] = useState(true);
  
  const notifications = useNewSystem ? (newNotifications.data || []) : oldNotifications;
  const notificationsLoading = useNewSystem ? newNotifications.isLoading : oldNotificationsLoading;

  // Charger les notifications avec l'ancien système si nécessaire
  const loadNotifications = useCallback(async (): Promise<void> => {
    if (!user?.id || useNewSystem) return;

    try {
      setOldNotificationsLoading(true);

      // Essayer le cache d'abord
      const cacheKey = CacheService.generateKey('notifications', user.id);
      const cacheKeyCount = CacheService.generateKey('notifications_count', user.id);
      const cached = CacheService.get<ActivityNotification[]>(cacheKey);
      const cachedCount = CacheService.get<number>(cacheKeyCount);

      // ✅ Valider la cohérence du cache
      if (cached && cachedCount !== null) {
        // Vérifier que le cache est cohérent
        const isCacheCoherent = 
          cached.length === cachedCount || 
          (cached.length === 0 && cachedCount === 0);
        
        if (isCacheCoherent) {
          setOldNotifications(cached);
          setUnreadCount(cachedCount);
          setOldNotificationsLoading(false);
          return;
        } else {
          // ❌ Cache incohérent, invalider et continuer vers la DB
          logger.warn('Cache notifications incohérent, invalidation', {
            cachedLength: cached.length,
            cachedCount,
            userId: user.id
          });
          CacheService.invalidate(cacheKey);
          CacheService.invalidate(cacheKeyCount);
        }
      }

      // Charger le COUNT exact des notifications non lues avec protection auth
      const { count, error: countError } = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from('activity_notifications')
          .select('*', { count: 'exact', head: true })
          .eq('recruiter_id', user.id)
          .eq('is_read', false);
      }, 'dashboard.notifications_count');

      if (countError) {
        logger.error('Erreur COUNT notifications', countError);
      }

      const totalUnread = count || 0;
      logger.info('Notifications count loaded', { totalUnread, userId: user.id });
      setUnreadCount(totalUnread);

      // Charger les 20 premières notifications pour l'affichage avec protection auth
      const { data } = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from('activity_notifications')
          .select('*')
          .eq('recruiter_id', user.id)
          .eq('is_read', false)
          .order('created_at', { ascending: false })
          .limit(20);
      }, 'dashboard.notifications_list');

      const notificationsData = (data || []) as unknown as ActivityNotification[];
      
      // Mettre en cache (1 minute)
      CacheService.set(cacheKey, notificationsData, 60 * 1000);
      CacheService.set(cacheKeyCount, totalUnread, 60 * 1000);
      
      setOldNotifications(notificationsData);

    } catch (_error) {
      logger.error('Erreur chargement notifications', _error as Error);
    } finally {
      setOldNotificationsLoading(false);
    }
  }, [user?.id, useNewSystem]);

  // Charger les notifications au montage et avec polling (ancien système seulement)
  useEffect(() => {
    if (!useNewSystem) {
      // ✅ Invalider le cache au montage si incohérent
      if (user?.id) {
        const cacheKey = CacheService.generateKey('notifications', user.id);
        const cacheKeyCount = CacheService.generateKey('notifications_count', user.id);
        const cached = CacheService.get<ActivityNotification[]>(cacheKey);
        const cachedCount = CacheService.get<number>(cacheKeyCount);
        
        // Invalider si incohérent
        if (cached && cachedCount !== null && cached.length !== cachedCount) {
          logger.warn('Cache notifications incohérent au montage, invalidation', {
            cachedLength: cached.length,
            cachedCount,
            userId: user.id
          });
          CacheService.invalidate(cacheKey);
          CacheService.invalidate(cacheKeyCount);
        }
      }
      
      loadNotifications();
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
    return undefined; // Retourner undefined explicitement si nouveau système
  }, [loadNotifications, useNewSystem, user?.id]);

  // Mettre à jour unreadCount depuis le nouveau système
  useEffect(() => {
    if (useNewSystem && newNotifications.data) {
      setUnreadCount(newNotifications.data.length);
    }
  }, [useNewSystem, newNotifications.data]);

  const markNotificationAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from('activity_notifications')
          .update({ is_read: true })
          .eq('id', notificationId);
      }, 'dashboard.mark_notification_read');

      if (error) {
        throw error;
      }

      // Mettre à jour localement
      setUnreadCount(prev => Math.max(0, prev - 1));

      // Invalider le cache
      const cacheKey = CacheService.generateKey('notifications', user?.id || '');
      const cacheKeyCount = CacheService.generateKey('notifications_count', user?.id || '');
      CacheService.invalidate(cacheKey);
      CacheService.invalidate(cacheKeyCount);

      // Refetch selon le système
      if (useNewSystem) {
        newNotifications.refetch();
      }

    } catch (_error) {
      logger.error('Failed to mark notification as read:', _error as Error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from('activity_notifications')
          .update({ is_read: true })
          .eq('recruiter_id', user.id)
          .eq('is_read', false);
      }, 'dashboard.mark_all_notifications_read');

      if (error) {
        throw error;
      }

      // Mettre à jour localement
      setUnreadCount(0);

      // Invalider le cache
      const cacheKey = CacheService.generateKey('notifications', user.id);
      const cacheKeyCount = CacheService.generateKey('notifications_count', user.id);
      CacheService.invalidate(cacheKey);
      CacheService.invalidate(cacheKeyCount);

      // Refetch selon le système
      if (useNewSystem) {
        newNotifications.refetch();
      }

    } catch (_error) {
      logger.error('Failed to mark all notifications as read:', _error as Error);
    }
  };

  const refetchNotifications = () => {
    if (useNewSystem) {
      newNotifications.refetch();
    } else {
      // Invalider le cache et recharger
      const cacheKey = CacheService.generateKey('notifications', user?.id || '');
      const cacheKeyCount = CacheService.generateKey('notifications_count', user?.id || '');
      CacheService.invalidate(cacheKey);
      CacheService.invalidate(cacheKeyCount);
      loadNotifications();
    }
  };

  return {
    stats,
    campaigns,
    notifications,
    unreadCount,
    campaignsLoading,
    campaignsFetched, // ✅ Exposer pour gate l'empty state
    notificationsLoading,
    refetchCampaigns: refreshCampaigns,
    refetchNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    isRealtimeConnected: isConnected, // Toujours true avec polling
  };
};